import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { LIB_CONFIG, LibConfig } from '../../lib.config';
import { LoaderService } from '../../services/loader.service';
import { ScreenSizeService, ViewMode } from '../../services/screen-size';
import { BerufeService } from '../../services/suchseite/berufe.service';
import { BildungszielService } from '../../services/suchseite/bildungsziel.service';
import { ReferrerService } from '../../services/suchseite/referrer.service';
import { BildungsbereichauswahlComponent } from './bildungsbereichauswahl/bildungsbereichauswahl.component';
import { TextsucheComponent } from './textsuche/textsuche.component';

@Component({
  selector: 'now-wbsuche-lib-suchleiste',
  templateUrl: './suchleiste.component.html',
  styleUrls: ['suchleiste.component.scss'],
  imports: [NgClass, TextsucheComponent, BildungsbereichauswahlComponent, AsyncPipe]
})
export class SuchleisteComponent implements OnChanges {
  private readonly bildungszielService = inject(BildungszielService);
  private readonly loaderService = inject(LoaderService);
  private readonly screenSizeService = inject(ScreenSizeService);
  berufeService = inject(BerufeService);
  referrerService = inject(ReferrerService);
  private readonly libConfig = inject<LibConfig>(LIB_CONFIG);

  @Input()
  sucheMitBildungszielen!: boolean;

  @Input()
  bildungsziele: number[] = [];

  @Input()
  public textSucheInputElementId = '';

  @Output()
  dataChange = new EventEmitter<void>();

  public angeboteLoading$ = this.loaderService.bildungsangeboteLoading$;

  public isDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.DESKTOP);
  public isMobile$ = this.screenSizeService.rangeTo$(ViewMode.TABLET);
  public isPhone$ = this.screenSizeService.onlyPhone$;
  public showOrtsunabhaengig = this.libConfig?.showOrtsunabhaengig ?? true;
  public context$ = combineLatest([
    this.referrerService.origin$,
    this.referrerService.berufeRef$,
    this.berufeService.bezeichnung$
  ]).pipe(
    map(([origin, ref, bezeichnung]) => {
      const defaultTextWithoutBezeichnung = (helptext: string) =>
        bezeichnung
          ? helptext
          : 'Bei einer erneuten Sucheingabe wird nur der eingegebene Suchbegriff berücksichtigt.';
      switch (origin) {
        case 'NEWPLAN':
          return {
            url: !!ref ? `${this.libConfig.newPlanBasePath}${ref}` : '',
            linkText: 'Zurück zu New Plan',
            helpText: defaultTextWithoutBezeichnung(
              'Bei einer erneuten Sucheingabe wird nur das eingegebene Suchwort und nicht mehr das Ergebnis Ihres New Plan-Online-Tests berücksichtigt.'
            )
          };
        case 'BNET':
          return {
            url: !!ref ? `${this.libConfig.baBasePath}${ref}` : '',
            linkText: 'Zurück zu BERUFENET',
            helpText: defaultTextWithoutBezeichnung(
              'Bei einer erneuten Sucheingabe wird nur das eingegebene Suchwort und nicht mehr der zuvor über die Berufsperspektiven ausgewählte Beruf berücksichtigt.'
            )
          };
        default:
          return {
            url: null,
            linkText: null,
            helpText: defaultTextWithoutBezeichnung(
              'Bei einer erneuten Sucheingabe wird nur dieses eingegebene Suchwort berücksichtigt.'
            )
          };
      }
    })
  );

  ngOnChanges(changes: SimpleChanges) {
    if (changes.bildungsziele?.currentValue) {
      const bildungsziele = changes.bildungsziele?.currentValue;

      if (bildungsziele && bildungsziele.length > 0) {
        // bei Einsprung aus SET-E wird Oberknoten-Param gesetzt und darauf reagiert, indem der Oberknoten aufgelöst wird
        this.bildungszielService.setzeOberknoten(bildungsziele);
      }
    }
  }
}
